/**
 * Calendario assenze con supporto Desiderata
 * Assenze: click singolo o drag per selezionare, poi popup per tipo assenza / rimozione
 */

import { getState, addAbsence, deleteAbsence, setDesiderata, getDesiderataForDoctorDate, isDoctorAbsent, getSlotBlocksForDoctorDate, setAbsencesCalendarWeekRange, getEffectiveAbsences, addRecurringOverride, canEditAbsencesFor, canExportAbsencesRequestsPdf, canManageAbsenceRequestLocks, lockAbsenceRequestWeek, unlockAbsenceRequestWeek } from '../store.js';
import { getAbsenceCalendarCellLabel } from '../absenceCellLabel.js';
import { ABSENCE_CATEGORIES, ABSENCE_CATEGORY_LABELS, DESIDERATA_LABELS, DESIDERATA_TYPES, SLOT_ABSENCE_TYPES, SLOT_ABSENCE_LABELS, HOURS_PER_DAY } from '../types.js';
import { exportAbsenceDesiderataRequestsPdf } from '../pdfExport.js';
import { computeWeeklyBalanceForRange, getWeekStartsInRange } from '../workload.js';
import { getDaysInWeekRange, getMondayOfWeek, getWeekStart, weekRangeSelectorHTML, setupWeekRangeSelector, getFestiviInfrasettimanali, formatDateIt, getISOWeekString, confirmInModal, clearNativeTextSelection, PDF_DOWNLOAD_ICON_HTML, getItalianMonthGroupsForDays } from '../utils.js';

let currentDoctorFilter = null;

/** Popup Aggiungi assenza: campi espliciti data inizio / fine per queste categorie (precompilati dalla selezione). */
const ABSENCE_MODAL_EXPLICIT_DATE_RANGE_CATEGORIES = new Set([
  ABSENCE_CATEGORIES.CONGEDO_ORDINARIO,
  ABSENCE_CATEGORIES.CONGEDO_ORDINARIO_ANNO_PRECEDENTE,
  ABSENCE_CATEGORIES.RISCHIO_RADIOLOGICO,
  ABSENCE_CATEGORIES.PERMESSO,
  ABSENCE_CATEGORIES.CONGEDO_MALATTIA,
]);

function absenceCategoryNeedsModalDateRange(cat) {
  return ABSENCE_MODAL_EXPLICIT_DATE_RANGE_CATEGORIES.has(cat);
}

/** Formazione: solo M/P o intera giornata; ore accreditate 6h (M o P) o 7,6h per ogni giorno del periodo (sab, dom e festivi compresi) */
const FORMAZIONE_ORE_SLOT = 6;

/**
 * Touch: il dito che si muove scorre sempre la tabella; il tap fermo seleziona un giorno.
 * Per un periodo si usa «+ Più giorni» (primo giorno, poi ultimo), senza gesti a tempo.
 * Mouse/penna: pointer capture e trascinamento sulla riga.
 */
/** Tap: massimo spostamento tra pressione e rilascio per contare come click singolo. */
const ABSENCE_TOUCH_TAP_MAX_PX = 12;

/** Massimi giorni per anno solare (riepilogo assenze) */
const SUMMARY_MAX_DAYS = {
  [ABSENCE_CATEGORIES.CONGEDO_ORDINARIO]: 26,
  [ABSENCE_CATEGORIES.RISCHIO_RADIOLOGICO]: 5,
  [ABSENCE_CATEGORIES.PERMESSO]: 7,
  [ABSENCE_CATEGORIES.FORMAZIONE]: 12,
};

let _absenceSelectionCleanup = null;

/** Selezione «più giorni» in corso: primo giorno scelto, in attesa dell'ultimo. */
let _extendAnchor = null;
/** Ultima selezione conclusa: usata da shift+click su desktop. */
let _lastSelectionAnchor = null;
let _absencesSummaryYear = new Date().getFullYear();

/** Memoria scroll aggiornata da sync + listener: sopravvive a re-render consecutivi (setState + merge). */
let _absencesScrollMemory = {
  winX: 0,
  winY: 0,
  seTop: 0,
  seLeft: 0,
  mainLeft: 0,
  mainTop: 0,
  tableTop: 0,
  tableLeft: 0,
};

/** Dopo salvataggio assenza/desiderata: porta in vista la cella interessata (affidabile se i pixel falliscono). */
let _pendingAbsencesCellFocus = null;

let _absencesScrollHooksInstalled = false;

function syncScrollMemoryFromDOM(container) {
  if (typeof window === 'undefined' || !container) return;
  const tw = container.querySelector('.absences-table-wrapper');
  const se = document.scrollingElement || document.documentElement;
  _absencesScrollMemory = {
    winX: window.scrollX,
    winY: window.scrollY,
    seTop: se.scrollTop,
    seLeft: se.scrollLeft,
    mainLeft: container.scrollLeft,
    mainTop: container.scrollTop,
    tableTop: tw ? tw.scrollTop : _absencesScrollMemory.tableTop,
    tableLeft: tw ? tw.scrollLeft : _absencesScrollMemory.tableLeft,
  };
}

function ensureAbsencesScrollHooks() {
  if (_absencesScrollHooksInstalled) return;
  _absencesScrollHooksInstalled = true;
  const onScroll = () => {
    const main = document.getElementById('main-content');
    if (!main?.querySelector('.absences-table-wrapper')) return;
    syncScrollMemoryFromDOM(main);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
}

function restoreAbsencesScrollSnapshot(container, snap) {
  const apply = () => {
    const se = document.scrollingElement || document.documentElement;
    window.scrollTo(snap.winX ?? 0, snap.winY ?? 0);
    if (typeof snap.seTop === 'number') se.scrollTop = snap.seTop;
    if (typeof snap.seLeft === 'number') se.scrollLeft = snap.seLeft;
    container.scrollLeft = snap.mainLeft ?? 0;
    container.scrollTop = snap.mainTop ?? 0;
    const tw = container.querySelector('.absences-table-wrapper');
    if (tw) {
      tw.scrollTop = snap.tableTop ?? 0;
      tw.scrollLeft = snap.tableLeft ?? 0;
    }
  };
  apply();
  requestAnimationFrame(() => {
    apply();
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
  });
  setTimeout(apply, 0);
  setTimeout(apply, 32);
}

function scrollAbsencesCellIntoView(container, doctorId, dateStr) {
  if (!doctorId || !dateStr) return;
  const esc = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape : (s) => String(s).replace(/"/g, '\\"');
  const cell = container.querySelector(
    `.calendar-cell[data-doctor="${esc(doctorId)}"][data-date="${esc(dateStr)}"]`
  );
  if (cell) {
    cell.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    syncScrollMemoryFromDOM(container);
  }
}

function bindAbsenceRequestLockPanelOnce(container) {
  if (container.dataset.absenceRequestLockDelegate === '1') return;
  container.dataset.absenceRequestLockDelegate = '1';
  container.addEventListener('change', async (e) => {
    const inp = e.target;
    if (!(inp instanceof HTMLInputElement) || !inp.classList.contains('absence-request-lock-cb')) return;
    const ws = inp.dataset.week;
    if (!ws) return;
    const wantLock = inp.checked;
    inp.disabled = true;
    try {
      if (wantLock) await lockAbsenceRequestWeek(ws);
      else await unlockAbsenceRequestWeek(ws);
    } catch (err) {
      inp.checked = !wantLock;
      alert(err?.message || String(err));
    } finally {
      inp.disabled = false;
    }
  });
}

/** True se la data (YYYY-MM-DD) è feriale: lun-ven e non festivo infrasettimanale */
function isFeriale(dateStr, festiviSet) {
  const d = new Date(dateStr + 'T12:00:00');
  const wd = d.getDay();
  if (wd === 0 || wd === 6) return false;
  return !festiviSet.has(dateStr);
}

/** Conta i giorni in [start, end] che cadono in [yearStart, yearEnd]. Se onlyFeriali, conta solo lun-ven esclusi festivi. */
function countDaysInRange(start, end, yearStart, yearEnd, onlyFeriali, festiviSet) {
  const startD = start < yearStart ? yearStart : start;
  const endD = end > yearEnd ? yearEnd : end;
  if (startD > endD) return 0;
  let count = 0;
  const cur = new Date(startD + 'T12:00:00');
  const last = new Date(endD + 'T12:00:00');
  while (cur <= last) {
    const dateStr = cur.toISOString().slice(0, 10);
    if (onlyFeriali) {
      if (isFeriale(dateStr, festiviSet)) count++;
    } else {
      count++;
    }
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/** Calcola i totali giorni per medico per anno (1/1 - 31/12) per le categorie riepilogo. */
function computeAbsencesSummaryByYear(year) {
  const state = getState();
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const festivi = getFestiviInfrasettimanali(year);
  const absences = getEffectiveAbsences(yearStart, yearEnd);
  const doctors = state.doctors ?? [];
  const categories = [
    ABSENCE_CATEGORIES.CONGEDO_ORDINARIO,
    ABSENCE_CATEGORIES.CONGEDO_ORDINARIO_ANNO_PRECEDENTE,
    ABSENCE_CATEGORIES.RISCHIO_RADIOLOGICO,
    ABSENCE_CATEGORIES.PERMESSO,
    ABSENCE_CATEGORIES.FORMAZIONE,
  ];
  const result = {};
  for (const doc of doctors) {
    result[doc.id] = {
      doctor: doc,
      [ABSENCE_CATEGORIES.CONGEDO_ORDINARIO]: 0,
      [ABSENCE_CATEGORIES.CONGEDO_ORDINARIO_ANNO_PRECEDENTE]: 0,
      [ABSENCE_CATEGORIES.RISCHIO_RADIOLOGICO]: 0,
      [ABSENCE_CATEGORIES.PERMESSO]: 0,
      [ABSENCE_CATEGORIES.FORMAZIONE]: 0,
    };
  }
  for (const a of absences) {
    const cat = a.category || '';
    if (!categories.includes(cat)) continue;
    const start = (a.dateStart || '').slice(0, 10);
    const end = (a.dateEnd || a.dateStart || '').slice(0, 10) || start;
    const onlyFeriali = cat !== ABSENCE_CATEGORIES.FORMAZIONE;
    const days = countDaysInRange(start, end, yearStart, yearEnd, onlyFeriali, festivi);
    if (result[a.doctorId]) result[a.doctorId][cat] += days;
  }
  return result;
}

export function renderAbsencesCalendar(container) {
  const state = getState();
  const allDoctors = state.doctors ?? [];
  const doctors = (state.currentUser?.role === 'user' && state.currentUser?.doctorId)
    ? allDoctors.filter(d => d.id === state.currentUser.doctorId)
    : allDoctors;
  const defaultMon = getMondayOfWeek(new Date().toISOString().slice(0, 10));
  const weekFrom = state.absencesCalendarWeekFrom ?? defaultMon;
  const weekTo = state.absencesCalendarWeekTo ?? defaultMon;
  const days = getDaysInWeekRange(weekFrom, weekTo);
  const rangeStart = days[0]?.date;
  const rangeEnd = days[days.length - 1]?.date;
  const monthGroups = getItalianMonthGroupsForDays(days);
  const festivi = new Set();
  for (const d of days) {
    const y = parseInt(d.date.slice(0, 4), 10);
    getFestiviInfrasettimanali(y).forEach(fd => festivi.add(fd));
  }
  const isDayFestivo = (d) => d.weekday === 'sab' || d.weekday === 'dom' || festivi.has(d.date);

  const balances = computeWeeklyBalanceForRange(weekFrom, weekTo);
  const weekStarts = Object.keys(balances).sort();
  const balanceRowsHtml = weekStarts.map(ws => {
    const b = balances[ws];
    const iso = getISOWeekString(ws);
    const sun = new Date(ws + 'T12:00:00');
    sun.setDate(sun.getDate() + 6);
    const weekEnd = sun.toISOString().slice(0, 10);
    const diffClass = b.diff >= 0 ? 'balance-ok' : 'balance-deficit';
    const diffText = b.diff >= 0 ? `+${b.diff.toFixed(0)} h` : `${b.diff.toFixed(0)} h (straordinari)`;
    const slotText = `Slot: ${b.scheduledSlots.toFixed(1)} da coprire | ${b.availabilitySlots.toFixed(1)} disponibili | Bilancio ${b.balanceSlots >= 0 ? '+' : ''}${b.balanceSlots.toFixed(1)}`;
    return `<div class="balance-indicator-row ${diffClass}"><strong>${iso}</strong> (${formatDateIt(ws)} – ${formatDateIt(weekEnd)}): Ore da coprire ${b.scheduled.toFixed(0)} h | Disponibilità ${b.availability.toFixed(0)} h | Bilancio ${diffText} &nbsp;|&nbsp; ${slotText}</div>`;
  }).join('');

  ensureAbsencesScrollHooks();
  syncScrollMemoryFromDOM(container);
  const scrollSnapshot = { ..._absencesScrollMemory };

  container.innerHTML = `
    <div class="card absences-calendar-page${_extendAnchor ? ' absences-extend-active' : ''}">
      <div class="card-title">Calendario assenze e desiderata</div>
      <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 1rem;">
        Seleziona prima i giorni, poi scegli che cosa registrare: <strong>assenza</strong>, <strong>libera professione</strong>, <strong>formazione</strong> o <strong>desiderata</strong>.
        <strong>Un giorno:</strong> click o tap sulla casella. <strong>Più giorni:</strong> trascina sulla riga con il mouse, oppure apri una casella e premi «+ Più giorni», poi tocca l’ultimo giorno del periodo.
        Sul telefono il dito serve solo a scorrere la tabella: nessuna pressione prolungata. Le richieste per singole fasce (mattino, pomeriggio, notte) valgono solo per un giorno alla volta.
      </p>
      <div class="form-group" style="display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem;">
        <div>
          <label>Medico</label>
          <select id="filter-doctor">
            <option value="">Tutti i medici</option>
            ${allDoctors.map(d => `<option value="${d.id}">${d.cognome} ${d.nome} (${d.sigla})</option>`).join('')}
          </select>
        </div>
        <div>
          <label>Intervallo settimane</label>
          ${weekRangeSelectorHTML('absences-weeks', weekFrom, weekTo, { from: 'Settimana iniziale', to: 'Settimana finale' })}
        </div>
        ${canExportAbsencesRequestsPdf() ? `
        <div style="display: flex; align-items: flex-end;">
          <button type="button" class="btn btn-pdf" id="export-absences-requests-pdf" title="Tabella PDF: medici in riga, giorni in colonna (stesse sigle della griglia)">${PDF_DOWNLOAD_ICON_HTML}<span class="btn-pdf-label">PDF assenze</span></button>
        </div>` : ''}
      </div>
      <details class="balance-indicator-section balance-indicator-details" title="Stima preventiva: ore da coprire da slot ufficiali (prolungato contato come 6h / 1 slot); disponibilità = debito settimanale per medico da anagrafica, meno assenze; slot disponibili = ore ÷ 6h.">
        <summary class="balance-indicator-summary">Carico vs disponibilità per settimana</summary>
        <div class="balance-indicator-body">
          ${balanceRowsHtml}
        </div>
      </details>
      ${canManageAbsenceRequestLocks() ? (() => {
        const wks = getWeekStartsInRange(weekFrom, weekTo);
        const rows = wks.map((ws) => {
          const sun = new Date(ws + 'T12:00:00');
          sun.setDate(sun.getDate() + 6);
          const weekEnd = sun.toISOString().slice(0, 10);
          const locked = Boolean(state.absenceRequestLockedWeeks?.[ws]);
          const iso = getISOWeekString(ws);
          return `<label class="absence-request-lock-row">
      <input type="checkbox" class="absence-request-lock-cb" data-week="${ws}" ${locked ? 'checked' : ''} />
      <span><strong>${iso}</strong> ${formatDateIt(ws)} – ${formatDateIt(weekEnd)}</span>
    </label>`;
        }).join('');
        return `
      <details class="absence-request-lock-panel">
        <summary>Blocca richieste (assenze / desiderata) per settimana</summary>
        <p class="absence-request-lock-hint">Quando attivo, i medici non possono inserire o modificare assenze e desiderata per quella settimana. Admin, organizzatore e direttore possono sempre modificare.</p>
        <div class="absence-request-lock-toggles">${rows}</div>
      </details>`;
      })() : ''}
      <div class="table-wrapper absences-table-wrapper">
        <table class="calendar-table">
          <thead>
            ${days.length === 0 ? `
            <tr>
              <th class="calendar-col-medico" scope="col">Medico</th>
            </tr>
            ` : `
            <tr>
              <th rowspan="2" class="calendar-col-medico" scope="col">Medico</th>
              ${monthGroups.map(g => `<th class="calendar-month-header" colspan="${g.count}" scope="colgroup" title="${g.label}">${g.label}</th>`).join('')}
            </tr>
            <tr>
              ${days.map(d => `<th class="${isDayFestivo(d) ? 'day-festivo' : ''}" scope="col" title="${d.weekdayLabel}${d.weekday === 'lun' ? ' – ' + d.isoWeek : ''}">${d.day}<br><small>${d.weekdayShort}${d.weekday === 'lun' ? ' ' + d.isoWeek : ''}</small></th>`).join('')}
            </tr>
            `}
          </thead>
          <tbody>
            ${doctors.map(doc => `
              <tr data-doctor-id="${doc.id}">
                <td class="calendar-col-medico"><strong>${doc.sigla}</strong> ${doc.cognome}</td>
                ${days.map(d => `
                  <td class="calendar-cell-wrapper ${isDayFestivo(d) ? 'day-festivo' : ''}" data-doctor="${doc.id}" data-date="${d.date}">
                    <div class="calendar-cell available" data-doctor="${doc.id}" data-date="${d.date}" title="Seleziona per registrare assenza o desiderata">
                    </div>
                  </td>
                `).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      ${_extendAnchor ? `
      <div class="absences-extend-bar" role="status" aria-live="polite">
        <span class="absences-extend-bar-text">
          Periodo da <strong>${formatDateCompact(_extendAnchor.date)}</strong> — tocca l’ultimo giorno del periodo, sulla stessa riga
        </span>
        <button type="button" class="btn btn-secondary" id="absences-extend-cancel">Annulla</button>
      </div>` : ''}
      <p style="font-size: 0.8rem; color: var(--text-secondary); margin-top: 1rem;">
        <span style="display: inline-block; width: 16px; height: 16px; background: var(--danger); opacity: 0.7; vertical-align: middle;"></span> Assenza
        <span style="margin-left: 1rem;"><span style="display: inline-block; width: 16px; height: 16px; background: var(--warning); opacity: 0.6; vertical-align: middle;"></span> Desiderata</span>
        <span style="margin-left: 1rem;"><span style="display: inline-block; width: 16px; height: 16px; background: #0d9488; opacity: 0.8; vertical-align: middle;"></span> Libera Professione</span>
        <span style="margin-left: 1rem;"><span style="display: inline-block; width: 16px; height: 16px; background: #7c3aed; opacity: 0.8; vertical-align: middle;"></span> Formazione</span>
      </p>
      ${(() => {
        const role = state.currentUser?.role || '';
        const summaryScope = role.match(/^(admin|organizer|director)$/)
          ? 'all'
          : role === 'user' && state.currentUser?.doctorId
            ? 'self'
            : null;
        if (!summaryScope) return '';
        return (() => {
        const summary = computeAbsencesSummaryByYear(_absencesSummaryYear);
        let summaryDoctors = (state.doctors ?? []).slice().sort((a, b) => (a.cognome || '').localeCompare(b.cognome || ''));
        if (summaryScope === 'self') {
          summaryDoctors = summaryDoctors.filter(d => d.id === state.currentUser.doctorId);
        }
        const catCols = [
          ABSENCE_CATEGORIES.CONGEDO_ORDINARIO,
          ABSENCE_CATEGORIES.CONGEDO_ORDINARIO_ANNO_PRECEDENTE,
          ABSENCE_CATEGORIES.RISCHIO_RADIOLOGICO,
          ABSENCE_CATEGORIES.PERMESSO,
          ABSENCE_CATEGORIES.FORMAZIONE,
        ];
        const defaultSummaryRow = () => ({
          [ABSENCE_CATEGORIES.CONGEDO_ORDINARIO]: 0,
          [ABSENCE_CATEGORIES.CONGEDO_ORDINARIO_ANNO_PRECEDENTE]: 0,
          [ABSENCE_CATEGORIES.RISCHIO_RADIOLOGICO]: 0,
          [ABSENCE_CATEGORIES.PERMESSO]: 0,
          [ABSENCE_CATEGORIES.FORMAZIONE]: 0,
        });
        const rows = summaryDoctors.map(doc => {
          const row = { doctor: doc, ...defaultSummaryRow(), ...(summary[doc.id] || {}) };
          const cells = catCols.map(cat => {
            const val = row[cat] || 0;
            const max = SUMMARY_MAX_DAYS[cat];
            const hasCap = typeof max === 'number' && max > 0;
            const over = hasCap && val > max;
            const valHtml = over ? `<span class="summary-val-over">${val}</span>` : val;
            const inner = hasCap ? `${valHtml} / ${max} gg` : `${valHtml} gg`;
            const tip = over ? 'Superato il massimo' : (!hasCap ? 'Non concorre al tetto 26 gg del congedo ordinario' : '');
            return `<td class="summary-cell ${over ? 'summary-over' : ''}" title="${tip}">${inner}</td>`;
          }).join('');
          return `<tr><td class="calendar-col-medico"><strong>${doc.sigla}</strong> ${doc.cognome}</td>${cells}</tr>`;
        }).join('');
        const years = [_absencesSummaryYear - 1, _absencesSummaryYear, _absencesSummaryYear + 1];
        return `
      <div class="card summary-absences-card" style="margin-top: 1.5rem;">
        <div class="card-title">${summaryScope === 'self' ? 'Riepilogo assenze' : 'Riepilogo Assenze per medico'}</div>
        <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 0.75rem;">Giorni per anno solare (1/1 – 31/12). Congedo ord., Rischio e Permesso: solo giorni feriali; Formazione: tutti i giorni. Il congedo ordinario (anno precedente) è separato e non concorre al massimo di 26 giorni del congedo ordinario.${summaryScope === 'self' ? ' Dati riferiti al tuo profilo medico.' : ''}</p>
        <div class="form-group" style="margin-bottom: 0.75rem;">
          <label>Anno</label>
          <select id="absences-summary-year" class="form-control" style="width: auto;">
            ${years.map(y => `<option value="${y}" ${y === _absencesSummaryYear ? 'selected' : ''}>${y}</option>`).join('')}
          </select>
        </div>
        <div class="table-wrapper">
          <table class="calendar-table summary-absences-table">
            <thead>
              <tr>
                <th class="calendar-col-medico">Medico</th>
                <th>${ABSENCE_CATEGORY_LABELS[ABSENCE_CATEGORIES.CONGEDO_ORDINARIO]} (max 26)</th>
                <th>${ABSENCE_CATEGORY_LABELS[ABSENCE_CATEGORIES.CONGEDO_ORDINARIO_ANNO_PRECEDENTE]}</th>
                <th>${ABSENCE_CATEGORY_LABELS[ABSENCE_CATEGORIES.RISCHIO_RADIOLOGICO]}</th>
                <th>${ABSENCE_CATEGORY_LABELS[ABSENCE_CATEGORIES.PERMESSO]}</th>
                <th>${ABSENCE_CATEGORY_LABELS[ABSENCE_CATEGORIES.FORMAZIONE]}</th>
              </tr>
            </thead>
            <tbody id="absences-summary-tbody">
              ${rows}
            </tbody>
          </table>
        </div>
      </div>`;
        })();
      })()}
    </div>
  `;

  bindAbsenceRequestLockPanelOnce(container);

  const extendCancelBtn = container.querySelector('#absences-extend-cancel');
  if (extendCancelBtn) {
    extendCancelBtn.addEventListener('click', () => cancelExtendSelection(container));
  }

  // Populate cells with state (selezionare sempre il div.calendar-cell, non il td)
  doctors.forEach(doc => {
    days.forEach(d => {
      const cell = container.querySelector(`.calendar-cell[data-doctor="${doc.id}"][data-date="${d.date}"]`);
      if (!cell) return;
      const absent = isDoctorAbsent(doc.id, d.date);
      const slotBlocks = getSlotBlocksForDoctorDate(doc.id, d.date);
      const hasLibera = slotBlocks.liberaProfessione.length > 0;
      const hasFormazione = slotBlocks.formazione.length > 0;
      const desiderata = getDesiderataForDoctorDate(doc.id, d.date);
      const label = getAbsenceCalendarCellLabel(doc.id, d.date, rangeStart, rangeEnd);
      const reqLocked = state.currentUser?.role === 'user' && state.absenceRequestLockedWeeks?.[getWeekStart(d.date)];
      let cellClass = 'calendar-cell ';
      if (absent) cellClass += 'blocked';
      else if (hasFormazione) cellClass += 'formazione';
      else if (hasLibera) cellClass += 'libera-professione';
      else if (desiderata.length) cellClass += 'desiderata';
      else cellClass += 'available';
      if (reqLocked) cellClass += ' absence-request-locked-week';
      cell.className = cellClass;
      cell.textContent = label;
      const openHint = ' — clicca per modificare o rimuovere';
      if (absent) cell.title = 'Assenza' + openHint;
      else if (hasFormazione) cell.title = 'Formazione: ' + slotBlocks.formazione.map(s => SLOT_ABSENCE_LABELS[s]).join(', ') + openHint;
      else if (hasLibera) cell.title = 'Libera Professione: ' + slotBlocks.liberaProfessione.map(s => SLOT_ABSENCE_LABELS[s]).join(', ') + openHint;
      else if (desiderata.length) cell.title = 'Desiderata: ' + desiderata.map(t => DESIDERATA_LABELS[t] || t).join(', ') + openHint;
      else cell.title = 'Clicca per registrare assenza o desiderata';
      if (reqLocked) {
        cell.title = (cell.title || '') + ' — Settimana bloccata per le richieste (non modificabile)';
      }
      if (_extendAnchor && _extendAnchor.doctorId === doc.id && _extendAnchor.date === d.date) {
        cell.classList.add('selecting', 'extend-anchor');
      }
    });
  });

  // Filter by doctor (per user ruolo, default al proprio medico)
  const filterSelect = container.querySelector('#filter-doctor');
  if (state.currentUser?.role === 'user' && state.currentUser?.doctorId) {
    filterSelect.value = state.currentUser.doctorId;
    filterSelect.style.display = 'none';
    filterSelect.parentElement.querySelector('label').style.display = 'none';
  } else {
    filterSelect.value = currentDoctorFilter || '';
  }
  filterSelect.addEventListener('change', () => {
    currentDoctorFilter = filterSelect.value || null;
    container.querySelectorAll('tbody tr').forEach(row => {
      const docId = row.dataset.doctorId;
      row.style.display = !currentDoctorFilter || docId === currentDoctorFilter ? '' : 'none';
    });
  });

  const summaryYearSelect = container.querySelector('#absences-summary-year');
  if (summaryYearSelect) {
    summaryYearSelect.addEventListener('change', () => {
      _absencesSummaryYear = parseInt(summaryYearSelect.value, 10);
      renderAbsencesCalendar(container);
    });
  }
  // Intervallo settimane (il subscribe allo store provvede al re-render)
  // Defer per evitare conflitti DOM quando si sostituisce il contenuto durante l'evento change
  setupWeekRangeSelector(container, 'absences-weeks', (val) => {
    setTimeout(() => setAbsencesCalendarWeekRange(val.weekFrom, val.weekTo), 0);
  });

  const pdfBtn = container.querySelector('#export-absences-requests-pdf');
  if (pdfBtn) {
    pdfBtn.addEventListener('click', async () => {
      const st = getState();
      const wf = st.absencesCalendarWeekFrom ?? defaultMon;
      const wt = st.absencesCalendarWeekTo ?? defaultMon;
      pdfBtn.disabled = true;
      try {
        const res = await exportAbsenceDesiderataRequestsPdf({ weekFrom: wf, weekTo: wt });
        if (!res.ok) alert(res.error || 'Esportazione non riuscita');
      } finally {
        pdfBtn.disabled = false;
      }
    });
  }

  // Selezione unica (assenze e desiderata): pointer events, il tipo si sceglie dopo, nel popup
  setupCellSelection(container, days);

  restoreAbsencesScrollSnapshot(container, scrollSnapshot);
  if (_pendingAbsencesCellFocus) {
    const { doctorId, date } = _pendingAbsencesCellFocus;
    _pendingAbsencesCellFocus = null;
    scrollAbsencesCellIntoView(container, doctorId, date);
  }
}

/** Cella calendario da evento: il click può cadere sul td wrapper (padding), non solo sul div interno. */
function resolveCalendarCellFromEventTarget(target) {
  if (!target?.closest) return null;
  const cell = target.closest('.calendar-cell');
  if (cell) return cell;
  const wrap = target.closest('td.calendar-cell-wrapper');
  return wrap?.querySelector?.('.calendar-cell') || null;
}

/** Avvia la selezione «più giorni»: si è scelto il primo giorno, si attende l'ultimo. */
function startExtendSelection(container, doctorId, date) {
  _extendAnchor = { doctorId, date };
  ensureExtendSelectionKeyHook();
  renderAbsencesCalendar(container);
}

/** Annulla la selezione «più giorni» in corso. */
function cancelExtendSelection(container) {
  if (!_extendAnchor) return;
  _extendAnchor = null;
  renderAbsencesCalendar(container);
}

let _extendSelectionKeyHookInstalled = false;

/** Esc annulla la selezione «più giorni» (in quel momento nessun popup è aperto). */
function ensureExtendSelectionKeyHook() {
  if (_extendSelectionKeyHookInstalled) return;
  _extendSelectionKeyHookInstalled = true;
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !_extendAnchor) return;
    const main = document.getElementById('main-content');
    if (!main?.querySelector('.absences-table-wrapper')) return;
    cancelExtendSelection(main);
  });
}

function releaseAbsenceCellPointerCapture(cell, pointerId) {
  if (cell == null || pointerId == null) return;
  try {
    if (typeof cell.hasPointerCapture === 'function' && cell.hasPointerCapture(pointerId)) {
      cell.releasePointerCapture(pointerId);
    }
  } catch (_) {}
}

/**
 * Selezione celle: un solo motore per assenze e desiderata.
 * - mouse/penna: click = un giorno, click e trascina sulla riga = periodo, shift+click estende l'ultima selezione.
 * - touch: tap = un giorno; il dito che si muove scorre sempre la tabella (nessuna pressione prolungata).
 *   Per un periodo si usa «+ Più giorni»: si tocca il primo giorno, poi l'ultimo, con lo scroll libero fra i due.
 */
function setupCellSelection(container, days) {
  let start = null;
  let activePointerId = null;
  let isDragPointer = false;
  let touchDownX = 0;
  let touchDownY = 0;
  let touchMaxDisplacement = 0;
  let dragEndDate = null;
  let shiftFromDate = null;

  function getCellsInRange(doctorId, dateFrom, dateTo) {
    return days
      .filter(d => d.date >= dateFrom && d.date <= dateTo)
      .map(d => container.querySelector(`.calendar-cell[data-doctor="${doctorId}"][data-date="${d.date}"]`))
      .filter(Boolean);
  }

  function clearSelectionHighlight() {
    container.querySelectorAll('.calendar-cell.selecting').forEach(c => c.classList.remove('selecting'));
  }

  function buildRange(doctorId, dateA, dateB) {
    const [d1, d2] = [dateA, dateB].sort();
    return days.filter(d => d.date >= d1 && d.date <= d2).map(d => d.date).sort();
  }

  function highlightRange(doctorId, dateA, dateB) {
    const [d1, d2] = [dateA, dateB].sort();
    clearSelectionHighlight();
    getCellsInRange(doctorId, d1, d2).forEach(c => c.classList.add('selecting'));
  }

  /** Trascinamento con mouse/penna: la cella sotto il puntatore definisce l'altro estremo. */
  function refreshDragHighlight(clientX, clientY) {
    if (!start || !isDragPointer) return;
    let el = null;
    try {
      el = document.elementFromPoint(clientX, clientY);
    } catch (_) {}
    const cell = resolveCalendarCellFromEventTarget(el);
    if (cell && cell.dataset.doctor === start.doctorId) dragEndDate = cell.dataset.date;
    highlightRange(start.doctorId, shiftFromDate ?? start.date, dragEndDate ?? start.date);
  }

  function reset() {
    const cell = start?.cell;
    const pid = activePointerId;
    start = null;
    activePointerId = null;
    isDragPointer = false;
    dragEndDate = null;
    shiftFromDate = null;
    touchMaxDisplacement = 0;
    clearSelectionHighlight();
    releaseAbsenceCellPointerCapture(cell, pid);
  }

  function openSheet(doctorId, dates) {
    if (!dates.length) return;
    _lastSelectionAnchor = { doctorId, date: dates[dates.length - 1] };
    showCellActionSheet(container, doctorId, dates);
  }

  function onPointerDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.isPrimary === false) return;
    const cell = resolveCalendarCellFromEventTarget(e.target);
    if (!cell || !container.contains(cell)) return;
    clearNativeTextSelection();
    reset();
    start = { cell, doctorId: cell.dataset.doctor, date: cell.dataset.date };
    activePointerId = e.pointerId;
    dragEndDate = start.date;
    touchDownX = e.clientX;
    touchDownY = e.clientY;
    touchMaxDisplacement = 0;
    isDragPointer = e.pointerType !== 'touch';
    if (!isDragPointer) return;
    if (e.shiftKey && _lastSelectionAnchor?.doctorId === start.doctorId && !_extendAnchor) {
      shiftFromDate = _lastSelectionAnchor.date;
    }
    try {
      cell.setPointerCapture?.(e.pointerId);
    } catch (_) {}
    highlightRange(start.doctorId, shiftFromDate ?? start.date, start.date);
  }

  function onPointerMove(e) {
    if (!start || activePointerId !== e.pointerId) return;
    if (!isDragPointer) {
      /* Touch: nessun trascinamento di selezione, il dito serve a scorrere la tabella. */
      touchMaxDisplacement = Math.max(
        touchMaxDisplacement,
        Math.hypot(e.clientX - touchDownX, e.clientY - touchDownY)
      );
      return;
    }
    if (e.pointerType === 'mouse' && e.buttons !== 1) return;
    refreshDragHighlight(e.clientX, e.clientY);
  }

  function onPointerUp(e) {
    if (!start || activePointerId !== e.pointerId) return;
    const doctorId = start.doctorId;
    const startDate = start.date;
    let dates;

    if (!isDragPointer) {
      const moved = Math.max(
        touchMaxDisplacement,
        Math.hypot((e.clientX ?? touchDownX) - touchDownX, (e.clientY ?? touchDownY) - touchDownY)
      );
      reset();
      /* Il dito si è spostato: era uno scroll, non una selezione. */
      if (moved > ABSENCE_TOUCH_TAP_MAX_PX) return;
      dates = resolveDatesWithExtendAnchor(doctorId, startDate, [startDate]);
    } else {
      const from = shiftFromDate ?? startDate;
      const cx = typeof e.clientX === 'number' ? e.clientX : 0;
      const cy = typeof e.clientY === 'number' ? e.clientY : 0;
      let endDate = dragEndDate ?? startDate;
      try {
        const endCell = resolveCalendarCellFromEventTarget(document.elementFromPoint(cx, cy));
        if (endCell && endCell.dataset.doctor === doctorId) endDate = endCell.dataset.date;
      } catch (_) {}
      const dragged = endDate !== from;
      reset();
      dates = dragged
        ? buildRange(doctorId, from, endDate)
        : resolveDatesWithExtendAnchor(doctorId, startDate, buildRange(doctorId, from, endDate));
    }

    if (dates?.length) openSheet(doctorId, dates);
  }

  /** In modalità «più giorni» il tocco successivo chiude il periodo; su un'altra riga si ricomincia. */
  function resolveDatesWithExtendAnchor(doctorId, date, fallbackDates) {
    if (!_extendAnchor) return fallbackDates;
    const anchor = _extendAnchor;
    _extendAnchor = null;
    renderAbsencesCalendar(container);
    if (anchor.doctorId !== doctorId) return fallbackDates;
    return buildRange(doctorId, anchor.date, date);
  }

  function onPointerCancel(e) {
    if (!start || (activePointerId != null && activePointerId !== e.pointerId)) return;
    reset();
  }

  function onLostPointerCapture(e) {
    if (!start || e.pointerId !== activePointerId) return;
    reset();
  }

  function onSelectStartCalendar(e) {
    if (!e.target.closest?.('.absences-table-wrapper')) return;
    e.preventDefault();
  }

  if (_absenceSelectionCleanup) _absenceSelectionCleanup();
  container.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('pointermove', onPointerMove, true);
  document.addEventListener('pointerup', onPointerUp, true);
  document.addEventListener('pointercancel', onPointerCancel, true);
  container.addEventListener('lostpointercapture', onLostPointerCapture);
  container.addEventListener('selectstart', onSelectStartCalendar, true);
  _absenceSelectionCleanup = () => {
    container.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('pointermove', onPointerMove, true);
    document.removeEventListener('pointerup', onPointerUp, true);
    document.removeEventListener('pointercancel', onPointerCancel, true);
    container.removeEventListener('lostpointercapture', onLostPointerCapture);
    container.removeEventListener('selectstart', onSelectStartCalendar, true);
    _absenceSelectionCleanup = null;
  };
}

/* ============================================================
 * Popup unificato «Cosa vuoi registrare?»
 * Prima si sceglie il periodo sulla griglia, poi il tipo.
 * Sostituisce i vecchi popup separati Assenza / Desiderata.
 * ============================================================ */

/** Desiderata che coprono l'intera giornata: le uniche ammesse su una selezione di più giorni. */
const DESIDERATA_FULL_DAY_TYPES = [
  DESIDERATA_TYPES.NO_MATTINO,
  DESIDERATA_TYPES.NO_POMERIGGIO,
  DESIDERATA_TYPES.NO_NOTTE,
];

const SHEET_KIND_ORDER = ['absence', 'lp', 'formazione', 'desiderata'];

const SHEET_KINDS = {
  absence: {
    label: 'Assenza',
    sub: 'Giornata intera: congedo, permesso, malattia, rischio radiologico',
    cls: 'sheet-kind-absence',
  },
  lp: {
    label: 'Libera professione',
    sub: 'Attività fuori turno, su una o più fasce della giornata',
    cls: 'sheet-kind-lp',
    singleDayOnly: true,
  },
  formazione: {
    label: 'Formazione',
    sub: 'Corsi e congressi: le ore sono accreditate al monte orario',
    cls: 'sheet-kind-formazione',
  },
  desiderata: {
    label: 'Desiderata',
    sub: 'Preferenza, non è un’assenza: non toglie ore dal monte',
    cls: 'sheet-kind-desiderata',
  },
};

/** Categorie assenza a giornata intera offerte nel passo 2 (LP e Formazione hanno riquadri propri). */
const SHEET_ABSENCE_CATEGORIES = [
  ABSENCE_CATEGORIES.CONGEDO_ORDINARIO,
  ABSENCE_CATEGORIES.CONGEDO_ORDINARIO_ANNO_PRECEDENTE,
  ABSENCE_CATEGORIES.RISCHIO_RADIOLOGICO,
  ABSENCE_CATEGORIES.PERMESSO,
  ABSENCE_CATEGORIES.CONGEDO_MALATTIA,
];

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

/** «Giovedì 14 maggio 2026» */
function formatDateHuman(dateStr) {
  try {
    const d = new Date(dateStr + 'T12:00:00');
    const s = d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    return s.charAt(0).toUpperCase() + s.slice(1);
  } catch (_) {
    return formatDateIt(dateStr);
  }
}

/** «gio 14 mag» */
function formatDateCompact(dateStr) {
  try {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch (_) {
    return formatDateIt(dateStr);
  }
}

/** Tutte le date (YYYY-MM-DD) fra due estremi inclusi. */
function enumerateDates(start, end) {
  const out = [];
  if (!start || !end || start > end) return out;
  const cur = new Date(start + 'T12:00:00');
  const last = new Date(end + 'T12:00:00');
  let guard = 0;
  while (cur <= last && guard++ < 800) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/**
 * Assenze/LP/formazione e desiderata già presenti nelle date indicate.
 * Le assenze sono raggruppate per record (una riga per assenza, anche se copre più giorni).
 */
function collectSelectionContent(doctorId, dates) {
  const absenceItems = [];
  const byKey = new Map();
  if (dates.length) {
    const effective = getEffectiveAbsences(dates[0], dates[dates.length - 1]);
    for (const date of dates) {
      for (const a of effective) {
        if (a.doctorId !== doctorId) continue;
        const start = (a.dateStart || '').slice(0, 10);
        const end = (a.dateEnd || a.dateStart || '').slice(0, 10) || start;
        if (!start || date < start || date > end) continue;
        const isRecurring = typeof a.id === 'string' && a.id.startsWith('recurring:');
        const recurringId = isRecurring ? (a._recurringId || String(a.id).split(':')[1]) : null;
        const key = isRecurring ? `rec:${recurringId}` : `abs:${a.id}`;
        let item = byKey.get(key);
        if (!item) {
          item = {
            key,
            category: a.category,
            slotTypes: a.slotTypes || [],
            recordStart: start,
            recordEnd: end,
            isRecurring,
            recurringId,
            absenceId: isRecurring ? null : a.id,
            dates: [],
          };
          byKey.set(key, item);
          absenceItems.push(item);
        }
        item.dates.push(date);
      }
    }
  }
  const desMap = new Map();
  const desiderataItems = [];
  for (const date of dates) {
    for (const t of getDesiderataForDoctorDate(doctorId, date) || []) {
      let item = desMap.get(t);
      if (!item) {
        item = { key: `des:${t}`, type: t, dates: [] };
        desMap.set(t, item);
        desiderataItems.push(item);
      }
      item.dates.push(date);
    }
  }
  return { absenceItems, desiderataItems, isEmpty: absenceItems.length === 0 && desiderataItems.length === 0 };
}

function describeAbsenceItem(item) {
  const base = ABSENCE_CATEGORY_LABELS[item.category] || item.category;
  const slots = (item.slotTypes || []).map(s => SLOT_ABSENCE_LABELS[s] || s).join(', ');
  return slots ? `${base} (${slots})` : base;
}

function describeDesiderataItem(item) {
  return DESIDERATA_LABELS[item.type] || item.type;
}

function formatDateSpan(dateList) {
  if (!dateList?.length) return '';
  if (dateList.length === 1) return formatDateCompact(dateList[0]);
  return `${formatDateCompact(dateList[0])} → ${formatDateCompact(dateList[dateList.length - 1])} (${dateList.length} giorni)`;
}

/** True se il record va oltre le date selezionate: rimuovendolo si toccano anche giorni fuori periodo. */
function absenceItemExtendsBeyond(item, dates) {
  if (!dates.length) return false;
  return item.recordStart < dates[0] || item.recordEnd > dates[dates.length - 1];
}

async function removeAbsenceItem(item) {
  if (item.isRecurring) {
    for (const d of item.dates) {
      await addRecurringOverride(item.recurringId, d);
    }
    return;
  }
  if (item.absenceId != null) await deleteAbsence(item.absenceId);
}

async function removeDesiderataItem(doctorId, item) {
  for (const d of item.dates) {
    const current = getDesiderataForDoctorDate(doctorId, d) || [];
    await setDesiderata(doctorId, d, current.filter(t => t !== item.type));
  }
}

/**
 * Svuota le date indicate prima di registrare la nuova voce (regola «sostituisci»).
 * `skipDesiderata` quando la nuova voce è una desiderata: setDesiderata riscrive già la giornata.
 */
async function clearSelectionContent(doctorId, dates, content, skipDesiderata) {
  for (const item of content.absenceItems) {
    await removeAbsenceItem(item);
  }
  if (skipDesiderata) return;
  for (const d of dates) {
    if ((getDesiderataForDoctorDate(doctorId, d) || []).length) {
      await setDesiderata(doctorId, d, []);
    }
  }
}

/**
 * Popup unificato per la selezione corrente (una cella o un periodo sulla stessa riga).
 * Passo 1: che cosa registrare + voci già presenti. Passo 2: dettaglio e conferma.
 *
 * Richiamato anche dalla riga «Assenze/Desiderata» del Piano Turni, che passa `opts`:
 * - `doctorId` null e `opts.doctorPicker` → passo 0 con la scelta del medico;
 * - `opts.allowExtend: false` nasconde «+ Più giorni», che vive sul calendario assenze;
 * - `opts.focusItemKeys` evidenzia la voce su cui si è cliccato.
 */
export function showCellActionSheet(container, doctorId, dates, opts = {}) {
  if (!Array.isArray(dates) || dates.length === 0) return;
  const state = getState();
  const allowExtend = opts.allowExtend !== false;
  const focusItemKeys = new Set(opts.focusItemKeys || []);
  const pickerDoctors = Array.isArray(opts.doctorPicker) ? opts.doctorPicker : null;
  let doctor = (state.doctors ?? []).find(d => d.id === doctorId);
  const isMulti = dates.length > 1;
  const dateFrom = dates[0];
  const dateTo = dates[dates.length - 1];
  let readOnly = doctorId == null || !dates.every(d => canEditAbsencesFor(doctorId, d));

  let step = doctorId == null && pickerDoctors ? 0 : 1;
  let kind = null;
  let busy = false;
  const draft = {
    category: ABSENCE_CATEGORIES.CONGEDO_ORDINARIO,
    dateStart: dateFrom,
    dateEnd: dateTo,
    lpSlots: new Set(),
    formazioneMode: isMulti ? 'intera' : null,
    desiderata: new Set(isMulti ? DESIDERATA_FULL_DAY_TYPES : []),
  };

  clearNativeTextSelection();
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay absence-sheet-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.innerHTML = `
    <div class="modal absence-sheet">
      <div class="modal-header absence-sheet-header">
        <div class="absence-sheet-heading">
          <h3 id="sheet-title"></h3>
          <div class="absence-sheet-subtitle" id="sheet-subtitle"></div>
        </div>
        <button type="button" class="btn btn-secondary btn-sm" id="sheet-close" aria-label="Chiudi">×</button>
      </div>
      <div class="modal-body absence-sheet-body" id="sheet-body"></div>
      <div class="modal-footer absence-sheet-footer" id="sheet-footer"></div>
    </div>
  `;
  document.body.appendChild(overlay);

  const elTitle = overlay.querySelector('#sheet-title');
  const elSubtitle = overlay.querySelector('#sheet-subtitle');
  const elBody = overlay.querySelector('#sheet-body');
  const elFooter = overlay.querySelector('#sheet-footer');

  function close() {
    document.removeEventListener('keydown', onKeyDown, true);
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
  }

  function onKeyDown(e) {
    if (e.key !== 'Escape') return;
    if (busy) return;
    e.stopPropagation();
    if (step === 2) {
      step = 1;
      kind = null;
      renderSheet();
      return;
    }
    if (step === 1 && pickerDoctors) {
      backToDoctorPicker();
      return;
    }
    close();
  }

  /** Torna alla scelta del medico (solo quando il popup è stato aperto senza medico). */
  function backToDoctorPicker() {
    if (!pickerDoctors) return;
    step = 0;
    kind = null;
    doctorId = null;
    doctor = undefined;
    readOnly = true;
    focusItemKeys.clear();
    renderSheet();
  }

  /** Date effettivamente interessate: per le assenze a intervallo valgono i campi data del popup. */
  function getEffectiveDates() {
    if (kind === 'absence' && absenceCategoryNeedsModalDateRange(draft.category)) {
      return enumerateDates(draft.dateStart, draft.dateEnd);
    }
    return dates;
  }

  function setError(msg) {
    const box = overlay.querySelector('#sheet-error');
    if (!box) return;
    box.textContent = msg || '';
    box.style.display = msg ? 'block' : 'none';
  }

  /** Passo 0: scelta del medico, quando il popup è aperto dalla riga «Assenze/Desiderata» col chip «+». */
  function stepZeroHtml() {
    if (!pickerDoctors?.length) {
      return `
        <div class="absence-sheet-warning">
          <div class="absence-sheet-warning-title">Nessun medico disponibile</div>
        </div>`;
    }
    return `
      <div class="absence-sheet-section">
        <div class="absence-sheet-section-title">Per quale medico?</div>
        <div class="absence-sheet-doctors">
          ${pickerDoctors.map(d => `
            <button type="button" class="absence-sheet-doctor" data-pick-doctor="${escapeHtml(String(d.id))}">
              <strong>${escapeHtml(d.sigla || '')}</strong>
              <span>${escapeHtml([d.cognome, d.nome].filter(Boolean).join(' '))}</span>
              ${d.hint ? `<small>${escapeHtml(d.hint)}</small>` : ''}
            </button>`).join('')}
        </div>
        <p class="absence-sheet-hint">In grigio chi ha già un'assenza o una desiderata in questa data.</p>
      </div>`;
  }

  function currentContentHtml() {
    const content = collectSelectionContent(doctorId, dates);
    if (content.isEmpty) return '';
    const focusCls = (key) => (focusItemKeys.has(key) ? ' is-focused' : '');
    const rows = [
      ...content.absenceItems.map(it => `
        <li class="absence-sheet-item${focusCls(it.key)}">
          <span class="absence-sheet-item-text">
            <strong>${escapeHtml(describeAbsenceItem(it))}</strong>
            <small>${escapeHtml(formatDateSpan(it.dates))}${absenceItemExtendsBeyond(it, dates) ? ` — l’assenza completa va dal ${escapeHtml(formatDateCompact(it.recordStart))} al ${escapeHtml(formatDateCompact(it.recordEnd))}` : ''}</small>
          </span>
          ${readOnly ? '' : `<button type="button" class="btn btn-secondary btn-sm absence-sheet-remove" data-remove-abs="${escapeHtml(it.key)}">Rimuovi</button>`}
        </li>`),
      ...content.desiderataItems.map(it => `
        <li class="absence-sheet-item${focusCls(it.key)}">
          <span class="absence-sheet-item-text">
            <strong>${escapeHtml(describeDesiderataItem(it))}</strong>
            <small>${escapeHtml(formatDateSpan(it.dates))}</small>
          </span>
          ${readOnly ? '' : `<button type="button" class="btn btn-secondary btn-sm absence-sheet-remove" data-remove-des="${escapeHtml(it.key)}">Rimuovi</button>`}
        </li>`),
    ].join('');
    return `
      <div class="absence-sheet-section absence-sheet-current">
        <div class="absence-sheet-section-title">Già presente ${isMulti ? 'in queste date' : 'in questa data'}</div>
        <ul class="absence-sheet-items">${rows}</ul>
      </div>`;
  }

  function overwriteNoticeHtml(effDates) {
    const content = collectSelectionContent(doctorId, effDates);
    if (content.isEmpty) return '';
    const items = [
      ...content.absenceItems.map(it => {
        const beyond = absenceItemExtendsBeyond(it, effDates);
        return `<li>${escapeHtml(describeAbsenceItem(it))} — ${escapeHtml(formatDateSpan(it.dates))}${beyond ? ` <strong>(verrà rimossa per intero: ${escapeHtml(formatDateCompact(it.recordStart))} → ${escapeHtml(formatDateCompact(it.recordEnd))})</strong>` : ''}</li>`;
      }),
      ...content.desiderataItems.map(it => `<li>${escapeHtml(describeDesiderataItem(it))} — ${escapeHtml(formatDateSpan(it.dates))}</li>`),
    ].join('');
    return `
      <div class="absence-sheet-warning" id="sheet-overwrite">
        <div class="absence-sheet-warning-title">Attenzione: queste voci verranno rimosse e sostituite</div>
        <ul>${items}</ul>
      </div>`;
  }

  function kindTilesHtml() {
    return `
      <div class="absence-sheet-section">
        <div class="absence-sheet-section-title">Cosa vuoi registrare?</div>
        <div class="absence-sheet-kinds">
          ${SHEET_KIND_ORDER.map(id => {
            const k = SHEET_KINDS[id];
            const disabled = isMulti && k.singleDayOnly;
            return `
            <button type="button" class="absence-sheet-kind ${k.cls}${disabled ? ' is-disabled' : ''}" data-kind="${id}" ${disabled ? 'disabled' : ''}>
              <span class="absence-sheet-kind-dot" aria-hidden="true"></span>
              <span class="absence-sheet-kind-text">
                <strong>${escapeHtml(k.label)}</strong>
                <small>${disabled ? 'Disponibile solo selezionando un singolo giorno' : escapeHtml(k.sub)}</small>
              </span>
            </button>`;
          }).join('')}
        </div>
      </div>`;
  }

  function stepOneHtml() {
    if (readOnly) {
      return `
        ${currentContentHtml()}
        <div class="absence-sheet-warning">
          <div class="absence-sheet-warning-title">Settimana bloccata dal coordinamento</div>
          <p style="margin: 0.35rem 0 0;">Le richieste di assenze e desiderata per queste date non sono modificabili.</p>
        </div>`;
    }
    const extendBtn = (isMulti || !allowExtend) ? '' : `
      <button type="button" class="btn btn-secondary absence-sheet-extend" id="sheet-extend">+ Più giorni</button>`;
    return `
      ${currentContentHtml()}
      ${extendBtn}
      ${kindTilesHtml()}`;
  }

  function chipsHtml(list, selectedSet, attr) {
    return list.map(o => `
      <button type="button" class="absence-sheet-chip${selectedSet.has(o.value) ? ' selected' : ''}" ${attr}="${escapeHtml(o.value)}"${o.title ? ` title="${escapeHtml(o.title)}"` : ''}>
        <strong>${escapeHtml(o.label)}</strong>${o.hint ? `<small>${escapeHtml(o.hint)}</small>` : ''}
      </button>`).join('');
  }

  function stepTwoHtml() {
    const eff = getEffectiveDates();
    let inner = '';
    if (kind === 'absence') {
      inner = `
        <div class="absence-sheet-section">
          <div class="absence-sheet-section-title">Tipo di assenza</div>
          <div class="absence-sheet-radios">
            ${SHEET_ABSENCE_CATEGORIES.map(cat => `
              <label class="absence-sheet-radio${draft.category === cat ? ' selected' : ''}">
                <input type="radio" name="sheet-category" value="${cat}" ${draft.category === cat ? 'checked' : ''} />
                <span>${escapeHtml(ABSENCE_CATEGORY_LABELS[cat])}</span>
              </label>`).join('')}
          </div>
        </div>
        ${absenceCategoryNeedsModalDateRange(draft.category) ? `
        <div class="absence-sheet-section">
          <div class="absence-sheet-section-title">Periodo</div>
          <div class="absence-sheet-dates">
            <label for="sheet-date-start">Dal</label>
            <input type="date" id="sheet-date-start" value="${escapeHtml(draft.dateStart)}" />
            <label for="sheet-date-end">Al</label>
            <input type="date" id="sheet-date-end" value="${escapeHtml(draft.dateEnd)}" />
          </div>
          <p class="absence-sheet-hint">Date incluse. Puoi correggere il periodo se diverso da quello selezionato sul calendario.</p>
        </div>` : ''}`;
    } else if (kind === 'lp') {
      const opts = SLOT_ABSENCE_TYPES.map(s => ({ value: s, label: SLOT_ABSENCE_LABELS[s] }));
      inner = `
        <div class="absence-sheet-section">
          <div class="absence-sheet-section-title">Quali fasce sei impegnato?</div>
          <div class="absence-sheet-chips">${chipsHtml(opts, draft.lpSlots, 'data-lp-slot')}</div>
          <button type="button" class="btn btn-secondary btn-sm absence-sheet-allday" id="sheet-lp-allday">Seleziona tutta la giornata</button>
        </div>`;
    } else if (kind === 'formazione') {
      const modes = isMulti
        ? [{ value: 'intera', label: 'Intera giornata', hint: `${HOURS_PER_DAY}h per ogni giorno del periodo` }]
        : [
            { value: 'mattino', label: 'Mattino', hint: `${FORMAZIONE_ORE_SLOT}h accreditate` },
            { value: 'pomeriggio', label: 'Pomeriggio', hint: `${FORMAZIONE_ORE_SLOT}h accreditate` },
            { value: 'intera', label: 'Intera giornata', hint: `${HOURS_PER_DAY}h accreditate` },
          ];
      const sel = new Set(draft.formazioneMode ? [draft.formazioneMode] : []);
      inner = `
        <div class="absence-sheet-section">
          <div class="absence-sheet-section-title">Quando sei in formazione?</div>
          <div class="absence-sheet-chips">${chipsHtml(modes, sel, 'data-form-mode')}</div>
          ${isMulti ? '<p class="absence-sheet-hint">Su più giorni la formazione copre sempre l’intera giornata (sabato, domenica e festivi compresi).</p>' : ''}
        </div>`;
    } else {
      const opts = isMulti
        ? [{ value: '__intera__', label: 'Intera giornata', hint: 'Niente mattino, pomeriggio o notte' }]
        : [
            { value: DESIDERATA_TYPES.NO_MATTINO, label: 'NO Mattino', hint: 'Non assegnarmi al mattino' },
            { value: DESIDERATA_TYPES.NO_POMERIGGIO, label: 'NO Pomeriggio', hint: 'Non assegnarmi al pomeriggio' },
            { value: DESIDERATA_TYPES.NO_NOTTE, label: 'NO Notte', hint: 'Non assegnarmi la notte' },
            { value: DESIDERATA_TYPES.NO_REPERIBILITA_GIORNO, label: 'NO Reperibilità giorno', hint: 'Solo reperibilità diurna' },
            { value: DESIDERATA_TYPES.NO_REPERIBILITA_NOTTE, label: 'NO Reperibilità notte', hint: 'Solo reperibilità notturna' },
          ];
      const sel = isMulti ? new Set(['__intera__']) : draft.desiderata;
      inner = `
        <div class="absence-sheet-section">
          <div class="absence-sheet-section-title">Da cosa vuoi essere escluso?</div>
          <div class="absence-sheet-chips">${chipsHtml(opts, sel, 'data-des-type')}</div>
          ${isMulti
            ? '<p class="absence-sheet-hint">Su più giorni si può chiedere solo la giornata intera. Per le singole fasce (e per le reperibilità) seleziona un giorno alla volta.</p>'
            : '<button type="button" class="btn btn-secondary btn-sm absence-sheet-allday" id="sheet-des-allday">Seleziona tutta la giornata</button>'}
        </div>`;
    }
    return `${inner}${overwriteNoticeHtml(eff)}<p class="absence-sheet-error" id="sheet-error" style="display: none;"></p>`;
  }

  function confirmLabel() {
    const eff = getEffectiveDates();
    const n = eff.length;
    const giorni = n === 1 ? '1 giorno' : `${n} giorni`;
    const replacing = !collectSelectionContent(doctorId, eff).isEmpty;
    const verb = replacing ? 'Sostituisci con' : 'Registra';
    if (kind === 'absence') return `${verb} ${ABSENCE_CATEGORY_LABELS[draft.category]} — ${giorni}`;
    if (kind === 'lp') return `${verb} Libera professione — ${giorni}`;
    if (kind === 'formazione') return `${verb} Formazione — ${giorni}`;
    return `${verb} Desiderata — ${giorni}`;
  }

  function renderSheet() {
    if (step === 0) {
      elTitle.textContent = formatDateHuman(dateFrom);
      elSubtitle.textContent = 'Nuova assenza o desiderata';
      elBody.innerHTML = stepZeroHtml();
      elFooter.innerHTML = `<button type="button" class="btn btn-secondary" id="sheet-cancel">Chiudi</button>`;
      bindSheet();
      return;
    }
    if (step === 1) {
      elTitle.textContent = isMulti
        ? `${dates.length} giorni selezionati`
        : formatDateHuman(dateFrom);
      elSubtitle.textContent = isMulti
        ? `${formatDateCompact(dateFrom)} → ${formatDateCompact(dateTo)}${doctor ? ` · ${doctor.cognome} ${doctor.nome} (${doctor.sigla})` : ''}`
        : (doctor ? `${doctor.cognome} ${doctor.nome} (${doctor.sigla})` : '');
      elBody.innerHTML = stepOneHtml();
      elFooter.innerHTML = `
        ${pickerDoctors ? '<button type="button" class="btn btn-secondary" id="sheet-doctors-back">‹ Medici</button>' : ''}
        <button type="button" class="btn btn-secondary" id="sheet-cancel">Chiudi</button>`;
    } else {
      elTitle.textContent = SHEET_KINDS[kind].label;
      elSubtitle.textContent = isMulti
        ? `${formatDateCompact(dateFrom)} → ${formatDateCompact(dateTo)} · ${dates.length} giorni`
        : formatDateHuman(dateFrom);
      elBody.innerHTML = stepTwoHtml();
      elFooter.innerHTML = `
        <button type="button" class="btn btn-secondary" id="sheet-back">‹ Indietro</button>
        <button type="button" class="btn btn-primary" id="sheet-confirm">${escapeHtml(confirmLabel())}</button>`;
    }
    bindSheet();
  }

  function refreshStepTwoDynamic() {
    const box = overlay.querySelector('#sheet-overwrite');
    const eff = getEffectiveDates();
    const html = overwriteNoticeHtml(eff);
    const errEl = overlay.querySelector('#sheet-error');
    if (box) box.outerHTML = html || '<div id="sheet-overwrite" style="display: none;"></div>';
    else if (errEl) errEl.insertAdjacentHTML('beforebegin', html || '<div id="sheet-overwrite" style="display: none;"></div>');
    const confirmBtn = overlay.querySelector('#sheet-confirm');
    if (confirmBtn) confirmBtn.textContent = confirmLabel();
  }

  async function handleRemoveClick(btn) {
    const absKey = btn.dataset.removeAbs;
    const desKey = btn.dataset.removeDes;
    const content = collectSelectionContent(doctorId, dates);
    const item = absKey
      ? content.absenceItems.find(i => i.key === absKey)
      : content.desiderataItems.find(i => i.key === desKey);
    if (!item) return;
    const label = absKey ? describeAbsenceItem(item) : describeDesiderataItem(item);
    const beyond = absKey && absenceItemExtendsBeyond(item, dates);
    const msg = beyond
      ? `«${label}» copre l’intero periodo ${formatDateCompact(item.recordStart)} → ${formatDateCompact(item.recordEnd)}, oltre i giorni selezionati.\n\nVerrà rimossa per intero. Procedere?`
      : `Rimuovere «${label}» (${formatDateSpan(item.dates)})?`;
    if (!(await confirmInModal(msg, { title: 'Rimuovi', okLabel: 'Rimuovi', cancelLabel: 'Annulla' }))) return;
    busy = true;
    btn.disabled = true;
    try {
      _pendingAbsencesCellFocus = { doctorId, date: dates[0] };
      if (absKey) await removeAbsenceItem(item);
      else await removeDesiderataItem(doctorId, item);
    } catch (err) {
      console.error('showCellActionSheet remove', err);
      alert(err?.message || String(err));
    } finally {
      busy = false;
    }
    renderSheet();
  }

  function validateDraft() {
    if (kind === 'lp' && draft.lpSlots.size === 0) return 'Seleziona almeno una fascia oraria.';
    if (kind === 'formazione' && !draft.formazioneMode) return 'Seleziona Mattino, Pomeriggio oppure Intera giornata.';
    if (kind === 'desiderata' && draft.desiderata.size === 0) return 'Seleziona almeno una esclusione.';
    if (kind === 'absence' && absenceCategoryNeedsModalDateRange(draft.category)) {
      if (!draft.dateStart || !draft.dateEnd) return 'Indica la data di inizio e quella di fine.';
      if (draft.dateStart > draft.dateEnd) return 'La data di inizio non può essere successiva a quella di fine.';
    }
    return '';
  }

  async function handleConfirm(btn) {
    const err = validateDraft();
    if (err) {
      setError(err);
      return;
    }
    setError('');
    const eff = getEffectiveDates();
    if (!eff.length) {
      setError('Periodo non valido.');
      return;
    }
    if (eff.some(d => !canEditAbsencesFor(doctorId, d))) {
      setError('Una o più date ricadono in una settimana bloccata per le richieste.');
      return;
    }
    const content = collectSelectionContent(doctorId, eff);
    const beyond = content.absenceItems.filter(it => absenceItemExtendsBeyond(it, eff));
    if (beyond.length) {
      const list = beyond
        .map(it => `• ${describeAbsenceItem(it)}: ${formatDateCompact(it.recordStart)} → ${formatDateCompact(it.recordEnd)}`)
        .join('\n');
      const ok = await confirmInModal(
        `Queste assenze si estendono oltre i giorni selezionati e verranno rimosse per intero:\n\n${list}\n\nProcedere?`,
        { title: 'Sostituzione', okLabel: 'Procedi', cancelLabel: 'Annulla' }
      );
      if (!ok) return;
    }

    busy = true;
    btn.disabled = true;
    const prevLabel = btn.textContent;
    btn.textContent = 'Salvataggio…';
    try {
      _pendingAbsencesCellFocus = { doctorId, date: eff[0] };
      await clearSelectionContent(doctorId, eff, content, kind === 'desiderata');
      if (kind === 'desiderata') {
        const types = isMulti ? [...DESIDERATA_FULL_DAY_TYPES] : [...draft.desiderata];
        for (const d of eff) {
          await setDesiderata(doctorId, d, types);
        }
      } else {
        const payload = { doctorId, dateStart: eff[0], dateEnd: eff[eff.length - 1] };
        if (kind === 'absence') {
          payload.category = draft.category;
        } else if (kind === 'lp') {
          payload.category = ABSENCE_CATEGORIES.LIBERA_PROFESSIONE;
          payload.slotTypes = [...draft.lpSlots];
        } else {
          payload.category = ABSENCE_CATEGORIES.FORMAZIONE;
          if (draft.formazioneMode === 'mattino') {
            payload.slotTypes = ['mattino'];
            payload.oreFormazione = FORMAZIONE_ORE_SLOT;
          } else if (draft.formazioneMode === 'pomeriggio') {
            payload.slotTypes = ['pomeriggio'];
            payload.oreFormazione = FORMAZIONE_ORE_SLOT;
          } else {
            payload.slotTypes = ['mattino', 'pomeriggio'];
            payload.oreFormazione = HOURS_PER_DAY;
          }
        }
        await addAbsence(payload);
      }
      close();
    } catch (e) {
      console.error('showCellActionSheet confirm', e);
      setError(e?.message || String(e));
      btn.disabled = false;
      btn.textContent = prevLabel;
    } finally {
      busy = false;
    }
  }

  function bindSheet() {
    overlay.querySelectorAll('.absence-sheet-remove').forEach(btn => {
      btn.addEventListener('click', () => void handleRemoveClick(btn));
    });

    overlay.querySelectorAll('[data-pick-doctor]').forEach(btn => {
      btn.addEventListener('click', () => {
        const picked = btn.dataset.pickDoctor;
        const found = (getState().doctors ?? []).find(d => String(d.id) === String(picked));
        if (!found) return;
        doctorId = found.id;
        doctor = found;
        readOnly = !dates.every(d => canEditAbsencesFor(doctorId, d));
        step = 1;
        kind = null;
        renderSheet();
      });
    });

    const doctorsBackBtn = overlay.querySelector('#sheet-doctors-back');
    if (doctorsBackBtn) doctorsBackBtn.addEventListener('click', backToDoctorPicker);

    const extendBtn = overlay.querySelector('#sheet-extend');
    if (extendBtn) {
      extendBtn.addEventListener('click', () => {
        close();
        startExtendSelection(container, doctorId, dateFrom);
      });
    }

    overlay.querySelectorAll('.absence-sheet-kind').forEach(tile => {
      tile.addEventListener('click', () => {
        if (tile.disabled) return;
        kind = tile.dataset.kind;
        step = 2;
        renderSheet();
      });
    });

    const cancelBtn = overlay.querySelector('#sheet-cancel');
    if (cancelBtn) cancelBtn.addEventListener('click', close);

    const backBtn = overlay.querySelector('#sheet-back');
    if (backBtn) {
      backBtn.addEventListener('click', () => {
        step = 1;
        kind = null;
        renderSheet();
      });
    }

    overlay.querySelectorAll('input[name="sheet-category"]').forEach(inp => {
      inp.addEventListener('change', () => {
        draft.category = inp.value;
        renderSheet();
      });
    });

    const dStart = overlay.querySelector('#sheet-date-start');
    const dEnd = overlay.querySelector('#sheet-date-end');
    if (dStart) {
      dStart.addEventListener('change', () => {
        draft.dateStart = (dStart.value || '').slice(0, 10);
        refreshStepTwoDynamic();
      });
    }
    if (dEnd) {
      dEnd.addEventListener('change', () => {
        draft.dateEnd = (dEnd.value || '').slice(0, 10);
        refreshStepTwoDynamic();
      });
    }

    overlay.querySelectorAll('[data-lp-slot]').forEach(btn => {
      btn.addEventListener('click', () => {
        const s = btn.dataset.lpSlot;
        if (draft.lpSlots.has(s)) draft.lpSlots.delete(s);
        else draft.lpSlots.add(s);
        btn.classList.toggle('selected', draft.lpSlots.has(s));
        setError('');
      });
    });
    const lpAllDay = overlay.querySelector('#sheet-lp-allday');
    if (lpAllDay) {
      lpAllDay.addEventListener('click', () => {
        SLOT_ABSENCE_TYPES.forEach(s => draft.lpSlots.add(s));
        overlay.querySelectorAll('[data-lp-slot]').forEach(b => b.classList.add('selected'));
        setError('');
      });
    }

    overlay.querySelectorAll('[data-form-mode]').forEach(btn => {
      btn.addEventListener('click', () => {
        draft.formazioneMode = btn.dataset.formMode;
        overlay.querySelectorAll('[data-form-mode]').forEach(b => b.classList.toggle('selected', b === btn));
        setError('');
      });
    });

    overlay.querySelectorAll('[data-des-type]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (isMulti) return;
        const t = btn.dataset.desType;
        if (draft.desiderata.has(t)) draft.desiderata.delete(t);
        else draft.desiderata.add(t);
        btn.classList.toggle('selected', draft.desiderata.has(t));
        setError('');
      });
    });
    const desAllDay = overlay.querySelector('#sheet-des-allday');
    if (desAllDay) {
      desAllDay.addEventListener('click', () => {
        DESIDERATA_FULL_DAY_TYPES.forEach(t => draft.desiderata.add(t));
        overlay.querySelectorAll('[data-des-type]').forEach(b => {
          b.classList.toggle('selected', draft.desiderata.has(b.dataset.desType));
        });
        setError('');
      });
    }

    const confirmBtn = overlay.querySelector('#sheet-confirm');
    if (confirmBtn) confirmBtn.addEventListener('click', () => void handleConfirm(confirmBtn));
  }

  overlay.querySelector('#sheet-close').addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay && !busy) close();
  });
  document.addEventListener('keydown', onKeyDown, true);

  renderSheet();
}
